import { useEffect, useState } from "react";
import { Plus, FileDown } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATO = {
  pagato: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  in_attesa: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  rimborsato: "bg-white/10 text-white/60 border-white/20",
  fallito: "bg-[#ff0033]/15 text-[#ff5577] border-[#ff0033]/40",
};

export default function Finance() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ customer_id: "", importo: 0, metodo: "stripe", stato: "in_attesa", descrizione: "" });

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/payments"), api.get("/customers")]);
    setList(p.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", { ...form, importo: +form.importo });
      toast.success("Pagamento registrato");
      setShow(false);
      setForm({ customer_id: "", importo: 0, metodo: "stripe", stato: "in_attesa", descrizione: "" });
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const total = list.reduce((s, p) => s + (p.stato === "pagato" ? p.importo : 0), 0);
  const pending = list.reduce((s, p) => s + (p.stato === "in_attesa" ? p.importo : 0), 0);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Pagamenti</h1>
          <p className="text-sm text-white/45 mt-1">Stripe · PayPal · Bonifico · Contanti</p>
        </div>
        <button data-testid="pay-add" onClick={() => setShow(true)} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
          <Plus size={14} /> Nuovo Pagamento
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Incassato</div>
          <div className="font-display text-4xl font-extrabold neon-text mt-3">€{total.toLocaleString("it-IT")}</div>
        </div>
        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">In Attesa</div>
          <div className="font-display text-4xl font-extrabold text-amber-300 mt-3">€{pending.toLocaleString("it-IT")}</div>
        </div>
        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Transazioni</div>
          <div className="font-display text-4xl font-extrabold mt-3">{list.length}</div>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-4">Fattura</th><th className="text-left p-4">Cliente</th>
              <th className="p-4">Metodo</th><th className="p-4">Stato</th>
              <th className="p-4 text-right">Importo</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.map((p) => (
              <tr key={p.id} className="hover:bg-white/[0.02]" data-testid={`pay-${p.id}`}>
                <td className="p-4 font-mono text-xs">{p.fattura_num}</td>
                <td className="p-4">
                  <div className="font-medium">{p.customer?.nome} {p.customer?.cognome}</div>
                  <div className="text-xs text-white/40">{p.customer?.email}</div>
                </td>
                <td className="p-4 text-center text-xs uppercase tracking-wider">{p.metodo}</td>
                <td className="p-4 text-center">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${STATO[p.stato]}`}>{p.stato.replace('_', ' ')}</span>
                </td>
                <td className="p-4 text-right font-display font-extrabold neon-text">€{p.importo}</td>
                <td className="p-4 text-right">
                  <button className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/10 text-white/60 hover:text-white" onClick={() => toast.info("Export PDF in arrivo")}>
                    <FileDown size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg glass-strong rounded-3xl p-7" data-testid="pay-form">
            <div className="font-display text-2xl font-bold mb-5">Nuovo Pagamento</div>
            <div className="space-y-3">
              <select required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="">Cliente…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
              </select>
              <input required type="number" step="0.01" placeholder="Importo €" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.metodo} onChange={(e) => setForm({ ...form, metodo: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {["stripe", "paypal", "bonifico", "contanti"].map((o) => <option key={o}>{o}</option>)}
                </select>
                <select value={form.stato} onChange={(e) => setForm({ ...form, stato: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {["in_attesa", "pagato", "rimborsato", "fallito"].map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <input placeholder="Descrizione" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="pay-form-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">Crea</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
